<p-dialog header="Image Processing Pipeline"
          [(visible)]="visible"
          (onShow)="onDialogShow()"
          (onHide)="onDialogHide()"
          [style]="{width: '85vw', height: '80vh'}"
          [maximizable]="true"
          [modal]="true">

  <div class="pipeline-container">
    <!-- Left panel: pipeline steps table -->
    <div class="pipeline-left-panel">
      <div class="pipeline-toolbar">
        <p-button icon="pi pi-plus" size="small" [text]="true"
                  pTooltip="Add operation" tooltipPosition="bottom"
                  (click)="showOperationPicker = true"></p-button>
        <p-button icon="pi pi-save" size="small" [text]="true"
                  pTooltip="Save as preset" tooltipPosition="bottom"
                  (click)="showSavePresetDialog = true"
                  [disabled]="steps.length === 0"></p-button>
        <p-dropdown [options]="presets" optionLabel="name" optionValue="id"
                    [(ngModel)]="selectedPresetId"
                    (onChange)="loadPreset($event.value)"
                    placeholder="Load preset..." [showClear]="true"
                    styleClass="preset-dropdown" appendTo="body">
          <ng-template let-preset pTemplate="item">
            <div class="preset-item">
              <span class="preset-item-name">{{preset.name}}</span>
              <span class="preset-item-meta">{{getPresetStepCount(preset)}}
                <span *ngIf="getPresetDate(preset)"> · {{getPresetDate(preset)}}</span>
              </span>
            </div>
          </ng-template>
        </p-dropdown>
        <span class="flex-grow-1"></span>
        <p-button icon="pi pi-download" size="small" [text]="true"
                  pTooltip="Export all presets" tooltipPosition="bottom"
                  (click)="exportAllPresets()"
                  [disabled]="presets.length === 0"></p-button>
        <p-button icon="pi pi-upload" size="small" [text]="true"
                  pTooltip="Import presets" tooltipPosition="bottom"
                  (click)="importFileInput.click()"></p-button>
        <input #importFileInput type="file" accept=".json" style="display:none"
               (change)="onImportFile($event)"/>
      </div>

      <p-table [value]="steps" [scrollable]="true"
               styleClass="p-datatable-sm pipeline-steps-table"
               (onRowReorder)="onRowReorder($event)">
        <ng-template pTemplate="header">
          <tr>
            <th style="width:2rem"></th>
            <th style="width:2rem">#</th>
            <th style="width:1.5rem"></th>
            <th>Operation</th>
            <th style="width:5rem">Engine</th>
            <th style="width:6rem">Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-step let-ri="rowIndex">
          <tr [pReorderableRow]="ri"
              [class.selected-step]="ri === selectedStepIndex"
              [class.disabled-step]="!step.enabled"
              (click)="selectStep(ri)">
            <td>
              <span class="pi pi-bars" pReorderableRowHandle></span>
            </td>
            <td>{{ri + 1}}</td>
            <td>
              <i [class]="getStepStatusIcon(ri)"
                 [pTooltip]="getStepStatusTooltip(ri)"
                 tooltipPosition="right"
                 style="font-size: 0.8rem"></i>
            </td>
            <td>
              <span>{{getOperationName(step)}}</span>
            </td>
            <td>
              <span class="engine-badge">{{getEngineName(step)}}</span>
            </td>
            <td>
              <div class="flex gap-1 align-items-center">
                <button pButton pRipple icon="pi pi-arrow-up"
                        class="p-button-rounded p-button-text p-button-sm"
                        (click)="moveStep(ri, -1); $event.stopPropagation()"
                        [disabled]="ri === 0"></button>
                <button pButton pRipple icon="pi pi-arrow-down"
                        class="p-button-rounded p-button-text p-button-sm"
                        (click)="moveStep(ri, 1); $event.stopPropagation()"
                        [disabled]="ri === steps.length - 1"></button>
                <button pButton pRipple
                        [icon]="step.enabled ? 'pi pi-eye' : 'pi pi-eye-slash'"
                        class="p-button-rounded p-button-text p-button-sm"
                        (click)="toggleStep(ri); $event.stopPropagation()"
                        pTooltip="Toggle step"></button>
                <button pButton pRipple icon="pi pi-trash"
                        class="p-button-rounded p-button-text p-button-sm p-button-danger"
                        (click)="removeStep(ri); $event.stopPropagation()"></button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6" class="text-center text-color-secondary p-3">
              Click <i class="pi pi-plus"></i> to add operations to the pipeline.
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- Operation parameters for selected step -->
      <div *ngIf="selectedStepIndex >= 0 && getOperationDescriptor(steps[selectedStepIndex])"
           class="step-params-panel">
        <h4 class="mt-2 mb-2">Parameters: {{getOperationName(steps[selectedStepIndex])}}</h4>
        <div *ngIf="getOperationDescriptor(steps[selectedStepIndex])!.description"
             class="text-color-secondary text-sm mb-3">
          {{getOperationDescriptor(steps[selectedStepIndex])!.description}}
        </div>
        <div class="grid">
          <div *ngFor="let param of getVisibleParams(steps[selectedStepIndex])"
               class="col-12 md:col-6 py-1">
            <div class="param-row">
              <label class="param-label">
                {{param.label}}
                <i *ngIf="param.tooltip" class="pi pi-info-circle px-1 text-color-secondary"
                   [pTooltip]="param.tooltip" tooltipPosition="top"></i>
              </label>
              <div class="param-control" [ngSwitch]="param.type">
                <p-inputNumber *ngSwitchCase="'number'"
                               [ngModel]="steps[selectedStepIndex].params[param.id]"
                               (ngModelChange)="onParamChange(param.id, $event)"
                               [min]="param.min" [max]="param.max" [step]="param.step || 1"
                               [showButtons]="true" class="p-inputtext-sm w-full">
                </p-inputNumber>
                <p-checkbox *ngSwitchCase="'boolean'"
                            [ngModel]="steps[selectedStepIndex].params[param.id]"
                            (ngModelChange)="onParamChange(param.id, $event)"
                            [binary]="true">
                </p-checkbox>
                <p-dropdown *ngSwitchCase="'select'"
                            [options]="getParamOptions(param, steps[selectedStepIndex])"
                            [ngModel]="steps[selectedStepIndex].params[param.id]"
                            (ngModelChange)="onParamChange(param.id, $event)"
                            class="p-inputtext-sm w-full"
                            appendTo="body">
                </p-dropdown>
                <input *ngSwitchCase="'string'" pInputText class="p-inputtext-sm w-full"
                       [ngModel]="steps[selectedStepIndex].params[param.id]"
                       (ngModelChange)="onParamChange(param.id, $event)"/>
              </div>
            </div>
          </div>
        </div>
        <!-- Step action bar: hosts the in-progress indicator (progress bar OR
             spinner + status text), Cancel, and (for heavy ops only) the
             explicit Run button. Kept here in the param panel so it doesn't
             crowd the preview header / plot toolbar. -->
        <div class="step-action-bar flex align-items-center justify-content-end mt-2 gap-2"
             *ngIf="processing || getOperationDescriptor(steps[selectedStepIndex])?.requiresManualRun">
          <!-- In-progress indicator. Three states, picked in order:
                1) Active model download → determinate progress bar + MB caption.
                2) Engine status string (e.g. "Running inference (tile 3 / 9)…") → spinner + message.
                3) No status → bare spinner (other engines, or cellpose between phases). -->
          <ng-container *ngIf="processing">
            <ng-container *ngIf="modelDownload as md; else statusOrSpinnerTpl">
              <span class="model-download-progress flex-grow-1" style="min-width: 12rem;">
                <p-progressBar
                    [value]="md.pct ?? 0"
                    [showValue]="md.pct !== null"
                    [style]="{height: '0.75rem'}">
                </p-progressBar>
                <small class="muted">
                  Downloading model: {{md.loaded / 1024 / 1024 | number:'1.0-0'}} MB
                  <ng-container *ngIf="md.total"> / {{md.total / 1024 / 1024 | number:'1.0-0'}} MB
                  </ng-container>
                </small>
              </span>
            </ng-container>
            <ng-template #statusOrSpinnerTpl>
              <p-progressSpinner [style]="{width: '1.25rem', height: '1.25rem'}"
                                 strokeWidth="4">
              </p-progressSpinner>
              <span *ngIf="engineStatus" class="engine-status text-color-secondary text-sm">
                {{ engineStatus }}
              </span>
            </ng-template>
            <button pButton pRipple
                    [icon]="cancelling ? 'pi pi-spin pi-spinner' : 'pi pi-times'"
                    [label]="cancelling ? 'Cancelling…' : 'Cancel'"
                    class="p-button-rounded p-button-text p-button-sm p-button-danger"
                    [disabled]="cancelling"
                    (click)="cancelOperation()"
                    pTooltip="Cancel the current operation"
                    tooltipPosition="top">
            </button>
          </ng-container>
          <!-- "Parameters changed" hint + Run button: only for requiresManualRun
               ops, only when idle (during a run, the in-progress indicator
               above replaces them). -->
          <ng-container
            *ngIf="getOperationDescriptor(steps[selectedStepIndex])?.requiresManualRun && !processing">
            <small class="text-color-secondary"
                   *ngIf="stepStatuses[selectedStepIndex] === 'stale'">
              Parameters changed — click Run to apply.
            </small>
            <button pButton pRipple
                    icon="pi pi-play"
                    label="Run"
                    class="p-button-sm"
                    [disabled]="stepStatuses[selectedStepIndex] !== 'stale'"
                    (click)="runSelectedStep()"
                    pTooltip="Re-run this step with the current parameters"
                    tooltipPosition="top">
            </button>
          </ng-container>
        </div>
      </div>
    </div>

    <!-- Right panel: preview -->
    <div class="pipeline-right-panel">
      <div class="preview-header">
        <span *ngIf="selectedStepIndex >= 0">
          Step {{selectedStepIndex + 1}}: {{getOperationName(steps[selectedStepIndex])}}
        </span>
        <span *ngIf="selectedStepIndex < 0">Original image</span>
        <!-- NB: the in-progress indicator (progress bar / spinner + status /
             Cancel) used to live here. Moved to the per-step action bar in
             the param panel so the plot toolbar has its own space. -->

        <!-- Zoom/pan, region-draw, colormap & channel tools come from the
             embedded viewer's own toolbar (jax-image-visualization). -->
      </div>
      <app-pipeline-preview #preview class="preview-plot"></app-pipeline-preview>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="pipeline-footer">
      <div class="pipeline-footer-left">
        <span class="text-color-secondary text-sm">
          {{steps.length}} step{{steps.length !== 1 ? 's' : ''}}
          <span *ngIf="selectedStepIndex >= 0"> · Viewing step {{selectedStepIndex + 1}}</span>
        </span>
      </div>
      <div class="pipeline-footer-right">
        <p-button label="Run on Server" icon="pi pi-server"
                  [outlined]="true" size="small"
                  (click)="openRunOnServerDialog()"
                  [disabled]="!canRunOnServer()"
                  pTooltip="Run the full pipeline on the server (full-size image)">
        </p-button>
        <p-button label="Download" icon="pi pi-download"
                  size="small"
                  (click)="saveResult()"
                  [disabled]="!previewImage || processing"></p-button>
        <p-button label="Close" icon="pi pi-times" [text]="true"
                  size="small"
                  (click)="visible = false; visibleChange.emit(false)"></p-button>
      </div>
    </div>
  </ng-template>
</p-dialog>

<!-- Operation picker overlay -->
<p-dialog header="Add Operation" [(visible)]="showOperationPicker"
          [style]="{width: '30rem'}" [modal]="true">
  <div *ngFor="let entry of operationCategories | keyvalue" class="mb-3">
    <h4 class="capitalize mt-0 mb-2">{{entry.key}}</h4>
    <div class="flex flex-wrap gap-2">
      <div *ngFor="let op of entry.value" class="flex align-items-center gap-1">
        <p-button [label]="op.name" size="small" [outlined]="true"
                  [pTooltip]="op.description || ''" tooltipPosition="top"
                  (click)="addOperation(op)"
                  [badge]="getEngineLabel(op.engineId)" [badgeClass]="getEngineBadgeClass(op.engineId)">
        </p-button>
      </div>
    </div>
  </div>
  <div *ngIf="availableOperations.length === 0" class="text-color-secondary">
    No processing engines registered. Install an engine (e.g. OpenCV, Transformers.js)
    to see available operations.
  </div>
</p-dialog>

<!-- Save preset dialog -->
<p-dialog header="Save Pipeline Preset" [(visible)]="showSavePresetDialog"
          [style]="{width: '25rem'}" [modal]="true">
  <div class="flex flex-column gap-2">
    <label for="presetName">Preset name</label>
    <input id="presetName" pInputText [(ngModel)]="presetName" placeholder="My pipeline..."/>
  </div>
  <ng-template pTemplate="footer">
    <p-button label="Save" icon="pi pi-check" (click)="savePreset()"
              [disabled]="!presetName.trim()"></p-button>
    <p-button label="Cancel" icon="pi pi-times" [text]="true"
              (click)="showSavePresetDialog = false"></p-button>
  </ng-template>
</p-dialog>

<!-- Run on Server dialog -->
<p-dialog header="Run Pipeline on Server" [(visible)]="showRunOnServerDialog"
          [style]="{width: '30rem'}" [modal]="true" [closable]="!runningOnServer">
  <div class="flex flex-column gap-3">
    <p class="text-color-secondary text-sm mt-0">
      Submit the pipeline to the server for processing on the full-size image (GPU-accelerated).
    </p>
    <div class="flex flex-column gap-1">
      <label for="serverOutputFolder">Output folder</label>
      <input id="serverOutputFolder" pInputText [(ngModel)]="outputFolder"
             placeholder="path/in/bucket"/>
      <small class="text-color-secondary">Path inside the project bucket where each
        step's results are written. Defaults to the input image's parent directory;
        leave blank to use each tool's own default (typically an "output/" subfolder).</small>
    </div>
    <div class="flex flex-column gap-1">
      <label for="serverSuffix">Output file suffix</label>
      <input id="serverSuffix" pInputText [(ngModel)]="saveSuffix" placeholder="_cvai"/>
      <small class="text-color-secondary">Appended to the CVAI step's output filename
        (e.g. image{{saveSuffix}}.png). JIT tool steps use their own naming.</small>
    </div>
    <div class="text-sm">
      <strong>Operations ({{steps.length}} total):</strong>
      <ul class="mt-1 mb-0 pl-4">
        <li *ngFor="let step of steps; let i = index" [class.line-through]="!step.enabled">
          {{i + 1}}. {{getOperationName(step)}}
          <span class="text-color-secondary">({{getEngineName(step)}})</span>
        </li>
      </ul>
    </div>
    <div *ngIf="runningOnServer" class="flex align-items-center gap-2 mt-2">
      <i class="pi pi-spin pi-spinner text-blue-500"></i>
      <span class="text-sm">{{serverProgress}}</span>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <p-button label="Run" icon="pi pi-play" (click)="runOnServer()"
              [disabled]="runningOnServer" [loading]="runningOnServer"></p-button>
    <p-button label="Cancel" icon="pi pi-times" [text]="true"
              (click)="showRunOnServerDialog = false" [disabled]="runningOnServer"></p-button>
  </ng-template>
</p-dialog>

<p-confirmDialog></p-confirmDialog>
